import React, { useCallback, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert, Button, Form } from 'react-bootstrap';
import { ChevronLeft, Plus, Search } from 'lucide-react';
import { PageLoader } from '../../Components/LoadingSpinner';
import { BreadcrumbComponent } from '../../Components/BreadCrumb';
import CustomMultiSelect from '../../Components/CustomMultiSelect';
import AnimatedHeight from '../../Components/AnimatedHeight';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useTenantTerm } from '../../hooks/useTenantTerm';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { useIsSmallScreen } from '../../hooks/useIsSmallScreen';
import { useListDetailScroll } from '../../hooks/useListDetailScroll';
import { ClickModifiers, useOpenPath } from '../../hooks/useOpenPath';
import { useTenants } from '../../hooks/tenants';
import { useAllLeases } from '../../hooks/leases';
import { useAllParkingQueueEntries } from '../../hooks/parkingQueue';
import { useKeyRegister } from '../../hooks/keyRegister';
import { useFeature } from '../../hooks/useFeatures';
import { useBuildingsData } from '../../Helpers/BuildingSummariesContext';
import { naturalCompare } from '../../Helpers/naturalSort';
import { tenantsApi } from '../../api/tenantsApi';
import { TenantKind } from '../../types/Enums';
import { TenantInput } from '../../types/tenants';
import { translate } from '../../types/EnumTranslations';
import { buildingKeysFromParams } from '../Leases/leasesView';
import TenantModal from './TenantModal';
import TenantsTable from './TenantsTable';
import TenantDetailPane from './TenantDetailPane';
import {
    DEFAULT_TENANT_SORT,
    TENANT_KIND_FILTERS,
    TenantSort,
    filterTenants,
    nextTenantSort,
    sortTenantRows,
} from './tenantsView';
import './Tenants.css';
import '../Leases/Leases.css';

/**
 * Hyresgäster: every tenant (/tenants), under Hyresadmin, with the chosen one
 * in a pane beside the list (on phones: one at a time). `?building=`
 * (repeatable) is the Byggnad filter, `?tenant=` the open tenant; Typ, search
 * and the sort live for the session.
 */
const TenantsPage: React.FC = () => {
    const term = useTenantTerm();
    useDocumentTitle(term.plural);
    const [searchParams, setSearchParams] = useSearchParams();
    const buildingKeys = useMemo(() => buildingKeysFromParams(searchParams), [searchParams]);
    const selectedKey = searchParams.get('tenant') ?? undefined;
    const isAdmin = useIsAdmin();
    const isSmallScreen = useIsSmallScreen();
    const openPath = useOpenPath();
    const { tenants, tenantsByKey, loading, error, refresh } = useTenants();
    const { leases, loading: leasesLoading, error: leasesError } = useAllLeases();
    const { buildings: summaries } = useBuildingsData();
    // Queue places and keys for every tenant, read once here so switching tenant in the pane reads nothing.
    // Both roots are admin-only; the toggles hide their sections.
    const parkingEnabled = useFeature('parking');
    const keyRegisterEnabled = useFeature('keyRegister');
    const queue = useAllParkingQueueEntries(isAdmin && parkingEnabled);
    const keyRegister = useKeyRegister({ enabled: isAdmin && keyRegisterEnabled });

    const [search, setSearch] = useState('');
    const [kinds, setKinds] = useState<TenantKind[]>([]);
    const [sort, setSort] = useState<TenantSort>(DEFAULT_TENANT_SORT);
    const [showModal, setShowModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const buildings = useMemo(
        () => [...(summaries ?? [])].sort((a, b) => naturalCompare(a.name || '', b.name || '')),
        [summaries]
    );
    const buildingName = useCallback(
        (buildingKey: string) => buildings.find(b => b.key === buildingKey)?.name || 'Okänd byggnad',
        [buildings]
    );

    const selectedTenant = selectedKey ? tenantsByKey[selectedKey] : undefined;
    const mobileView = isSmallScreen && selectedKey ? 'detail' : 'list';
    const { containerRef, rememberListScroll } = useListDetailScroll<HTMLDivElement>(mobileView === 'detail');

    /** The URL with `change` applied; the tenant and building params share it. */
    const withParams = useCallback((change: (params: URLSearchParams) => void): URLSearchParams => {
        const next = new URLSearchParams(searchParams);
        change(next);
        return next;
    }, [searchParams]);

    const setBuildingFilter = (keys: string[]) => setSearchParams(withParams(params => {
        params.delete('building');
        // Every building chosen is the same as none: keep the URL short.
        if (keys.length < buildings.length) keys.forEach(key => params.append('building', key));
    }), { replace: true });

    // Opening a tenant pushes, so the browser back gesture closes it on phones.
    const openTenant = (tenantKey: string, event?: ClickModifiers) => {
        rememberListScroll();
        openPath(`/tenants?${withParams(params => params.set('tenant', tenantKey)).toString()}`, event);
    };
    const listSearch = withParams(params => params.delete('tenant')).toString();

    const rows = useMemo(
        () => filterTenants(tenants, leases, { buildingKeys, kinds, search }),
        [tenants, leases, buildingKeys, kinds, search]
    );
    const visibleRows = useMemo(() => sortTenantRows(rows, sort), [rows, sort]);

    const handleCreate = async (input: TenantInput) => {
        setSubmitting(true);
        try {
            const tenant = await tenantsApi.create(input);
            await refresh();
            setShowModal(false);
            setSearchParams(withParams(params => params.set('tenant', tenant.key)));
        } finally {
            setSubmitting(false);
        }
    };

    if ((loading && tenants.length === 0) || (leasesLoading && leases.length === 0)) return <PageLoader />;

    const singleBuildingKey = buildingKeys.length === 1 ? buildingKeys[0] : undefined;
    const buildingOptions = buildings.map(b => ({ label: b.name || 'Namnlös byggnad', value: b.key }));
    const kindOptions = TENANT_KIND_FILTERS.map(kind => ({ label: translate(kind), value: kind }));

    return (
        <div className="tenants-page">
            <div style={{ flexShrink: 0 }}>
                <BreadcrumbComponent
                    items={singleBuildingKey
                        ? [{ text: buildingName(singleBuildingKey), link: `/building/${singleBuildingKey}` }, { text: term.plural }]
                        : [{ text: term.plural }]}
                />
            </div>

            <div className="leases-content leases-content--wide tenants-content">
                {mobileView === 'list' && (
                    <>
                        <div className="leases-header">
                            <h1 className="page-title">{term.plural}</h1>
                        </div>

                        <div className="tenants-toolbar">
                            <div className="leases-filters">
                                <CustomMultiSelect
                                    label="Byggnad"
                                    options={buildingOptions}
                                    value={buildingKeys}
                                    onChange={value => setBuildingFilter(value as string[])}
                                    placeholder="Alla byggnader"
                                    selectedItemsLabel="{0} byggnader"
                                />
                                <CustomMultiSelect
                                    label="Typ"
                                    options={kindOptions}
                                    value={kinds}
                                    onChange={value => setKinds(value as TenantKind[])}
                                    placeholder="Alla typer"
                                    selectedItemsLabel="{0} typer"
                                />
                                <div className="leases-search">
                                    <Search size={16} className="leases-search-icon" aria-hidden="true" />
                                    <Form.Control
                                        type="search"
                                        placeholder="Sök namn, org.nr, e-post eller telefon"
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                        aria-label="Sök på namn, organisationsnummer, e-post eller telefon"
                                    />
                                </div>
                            </div>
                            {isAdmin && (
                                <Button variant="primary" className="leases-new-button" onClick={() => setShowModal(true)}>
                                    <Plus size={18} className="me-1" />Ny {term.singular.toLowerCase()}
                                </Button>
                            )}
                        </div>

                        {error && <Alert variant="danger">{error}</Alert>}
                        {leasesError && <Alert variant="danger">{leasesError}</Alert>}
                    </>
                )}

                <div className="tenants-split" ref={containerRef} data-mobile-view={mobileView}>
                    <div className="tenants-list-pane">
                        {visibleRows.length === 0 ? (
                            <p className="text-muted">
                                {tenants.length === 0
                                    ? `Inga ${term.plural.toLowerCase()} registrerade ännu.`
                                    : `Ingen ${term.singular.toLowerCase()} matchar filtret.`}
                            </p>
                        ) : (
                            <TenantsTable
                                rows={visibleRows}
                                sort={sort}
                                onSort={key => setSort(current => nextTenantSort(current, key))}
                                selectedKey={selectedKey}
                                buildingName={singleBuildingKey ? undefined : buildingName}
                                onOpen={openTenant}
                            />
                        )}
                    </div>

                    <div className="std-box tenants-detail-pane">
                        {mobileView === 'detail' && (
                            <Link to={{ search: listSearch }} className="tenants-back-button">
                                <ChevronLeft size={20} aria-hidden="true" />
                                Alla {term.plural.toLowerCase()}
                            </Link>
                        )}
                        {/* Outside the per-tenant key, so switching tenant eases the pane to its new height. */}
                        <AnimatedHeight>
                            {selectedTenant ? (
                                <TenantDetailPane
                                    key={selectedTenant.key}
                                    tenant={selectedTenant}
                                    leases={leases}
                                    leasesLoading={leasesLoading}
                                    buildingName={buildingName}
                                    queue={queue}
                                    keyRegister={keyRegister}
                                    refreshTenants={refresh}
                                    onDeleted={() => setSearchParams(withParams(params => params.delete('tenant')), { replace: true })}
                                />
                            ) : selectedKey && !loading ? (
                                <p className="text-muted">Personen hittades ej.</p>
                            ) : (
                                <div className="tenants-pane-empty text-muted">
                                    <p>Välj en {term.singular.toLowerCase()}</p>
                                    <p className="tenants-muted">
                                        {visibleRows.length} {(visibleRows.length === 1 ? term.singular : term.plural).toLowerCase()} i listan
                                    </p>
                                </div>
                            )}
                        </AnimatedHeight>
                    </div>
                </div>
            </div>

            <TenantModal show={showModal} onHide={() => setShowModal(false)} onSubmit={handleCreate} submitting={submitting} />
        </div>
    );
};

export default TenantsPage;
